import { Loader, SkeletonLoader, useIcon } from "@SeemplicityDev/shared";
import React, { useMemo } from "react";
import "./Chart.scss";
import WidgetError from "../../CustomizedDashboard/Widgets/WidgetError/WidgetError";

export const loaderTypes = {
  SPINNER: "spinner",
  SKELETON: "skeleton",
} as const;

export type LoaderType = (typeof loaderTypes)[keyof typeof loaderTypes];

export interface ChartProps {
  chartLoading: boolean;
  chartName: string;
  // chartError is only consumed via truthy checks; consumers pass an error
  // string, a boolean flag, or null/undefined.
  chartError: string | boolean | null;
  // chartData is only consumed via truthy checks; consumers pass either a
  // data object or a boolean, so it is intentionally untyped.
  chartData: unknown;
  hideLegend?: boolean;
  chartDivClassName?: string | null;
  loaderType?: LoaderType;
  chartContainerRef?: React.RefObject<HTMLDivElement> | null;
  chartReload?: (() => void) | null;
  emptyStateText?: string | null;
  emptyStateIcon?: string | null;
}

const Chart: React.FC<ChartProps> = ({
  chartLoading,
  chartName,
  chartError,
  chartData,
  hideLegend = false,
  chartDivClassName = null,
  loaderType = loaderTypes.SPINNER,
  chartContainerRef = null,
  chartReload = null,
  emptyStateText = null,
  emptyStateIcon = null,
}) => {
  const { getSvg } = useIcon();
  const errorExistsCondition = useMemo(() => {
    return chartError && !chartLoading;
  }, [chartError, chartLoading]);

  const noDataCondition = useMemo(() => {
    return !chartData && !chartError && !chartLoading;
  }, [chartError, chartData, chartLoading]);

  return (
    <div className={"chart-and-legend-wrap"} ref={chartContainerRef}>
      {chartLoading && (
        <>
          <div id={chartName} className="chart-wrap">
            {loaderType === loaderTypes.SPINNER && (
              <Loader
                center
                img={getSvg("icon-spinner")}
                iconSize={"xxxxl"}
                data-testid={"dashboard-loader"}
              />
            )}
            {loaderType === loaderTypes.SKELETON && (
              <SkeletonLoader data-testid={"dashboard-loader"} />
            )}
          </div>
          {!hideLegend && <div className={"legend-wrap"} />}
        </>
      )}
      {errorExistsCondition && <WidgetError onWidgetsReload={chartReload} />}
      {noDataCondition && (
        <Loader
          data-testid={"chart-no-data"}
          className={"empty-state-no-match-to-filter"}
          center
          noBackground
          icon={!!emptyStateIcon ? emptyStateIcon : "seem-icon-manualfinding"}
          subtitle={
            !!emptyStateText
              ? emptyStateText
              : "There are no Findings for this Scope and Filter"
          }
        />
      )}
      {!chartLoading && !errorExistsCondition && !noDataCondition && (
        <>
          <div
            id={chartName}
            className={`chart-wrap ${
              chartDivClassName ? chartDivClassName : ""
            }`}
          />
          {!hideLegend && (
            <div className={"legend-data-container"}>
              <div
                id={`${chartName}-legend-container`}
                className={`legend-wrap legend-wrap--${chartName}`}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Chart;
